/**
 * Which Sending Account a lead's outreach actually left from, as a label the
 * lead panel can show next to "Sent by <Rep>".
 *
 * One Sales Rep can have several connected accounts — several mailboxes, several
 * LinkedIn seats — and "Sent by <Rep>" alone cannot say which one sent to THIS
 * lead. This resolves that: the account, its human name, and the channel it
 * belongs to (so the label can read the mailbox address for email, the person's
 * name for LinkedIn).
 *
 * A lead's whole LinkedIn thread leaves from ONE account — the account that sent
 * the connection request is the only one LinkedIn will let message that person —
 * so any of the lead's LinkedIn jobs names it. Email is the same in practice: a
 * lead's thread is pinned to the mailbox that first wrote to them. A job that
 * has actually RUN is preferred over a merely-planned one: that is the account
 * the message truly came FROM.
 *
 * The account id lives in the SAME `linkedinAccountId` field for both channels
 * (an email job stores its mailbox there); the `channel` is what tells them
 * apart, and the name map now carries both LinkedIn seats and mailboxes, so an
 * email id resolves to its address instead of surfacing raw.
 */
export interface SendingAccountJob {
  leadId: string;
  channel?: string;
  status?: string;
  linkedinAccountId?: string;
}

export type SendingChannel = 'linkedin' | 'email';

const LINKEDIN_CHANNELS = new Set(['linkedin_connect', 'linkedin_dm']);
const EMAIL_CHANNELS = new Set(['email', 'email_followup']);
/** A send has happened or is in flight — this account really did the sending. */
const ACTED = new Set(['completed', 'running', 'simulated']);

function channelOf(raw: string | undefined): SendingChannel | null {
  if (LINKEDIN_CHANNELS.has(raw ?? '')) return 'linkedin';
  if (EMAIL_CHANNELS.has(raw ?? '')) return 'email';
  return null;
}

export function resolveLeadSendingAccount(
  jobs: readonly SendingAccountJob[],
  leadId: string,
  accountNames: Readonly<Record<string, string>>,
): { id: string; name: string; channel: SendingChannel } | null {
  const mine = jobs
    .filter(j => j.leadId === leadId && !!j.linkedinAccountId && channelOf(j.channel) !== null)
    .map(j => ({ id: j.linkedinAccountId!, channel: channelOf(j.channel)!, acted: ACTED.has(j.status ?? '') }));
  if (mine.length === 0) return null;
  // Precedence: a job that has RUN wins over a planned one (that account truly
  // sent). Between two run jobs, LinkedIn wins over email — a rare multi
  // campaign that touches a lead on both channels keeps naming its LinkedIn
  // seat, the connection-gated one where "which seat" is load-bearing, exactly
  // as before this covered email at all.
  const pick =
    mine.find(m => m.acted && m.channel === 'linkedin') ??
    mine.find(m => m.acted) ??
    mine.find(m => m.channel === 'linkedin') ??
    mine[0]!;
  return { id: pick.id, name: accountNames[pick.id] ?? pick.id, channel: pick.channel };
}
